// C3F — canonical decision for whether/which applicant interview surface to
// render on the worker dashboard.
//
// The applicant interview surface MUST be driven by the canonical
// `interview_state` from the backend, NOT solely by whether the onboarding
// wizard's active step happens to be `interview`. This module is the single
// source of that decision so it can be unit-tested and reused, guaranteeing
// exactly ONE interview card renders at any time.

// Every canonical interview state that requires applicant visibility.
export const INTERVIEW_JOURNEY_VISIBLE_STATES = new Set([
  'locked',
  'questionnaire_required',
  'uploads_required',
  'choose_slot',
  'awaiting_scheduling',
  'scheduled',
  'join_today',
  'awaiting_outcome',
  'under_review',
  'successful',
  'unsuccessful',
]);

/**
 * Whether the applicant interview journey should be visible at all.
 * @param {object} args
 * @param {boolean} args.isActiveEmployee  active employees don't see recruitment interview UI
 * @param {?{state?: string}} args.interviewState  canonical projection from backend
 * @param {boolean} [args.wizardShowsInterview]  defensive fallback for legacy payloads with no interview_state
 */
export function shouldShowInterviewJourney({ isActiveEmployee, interviewState, wizardShowsInterview = false }) {
  if (isActiveEmployee) return false;
  const stateKey = interviewState && interviewState.state ? interviewState.state : null;
  if (stateKey) return INTERVIEW_JOURNEY_VISIBLE_STATES.has(stateKey);
  // No canonical projection available (older payload) — fall back to the wizard.
  return Boolean(wizardShowsInterview);
}

// C3F.1 — next-action projection truth.
//
// Canonical states in which a "join your video interview" NEXT action is
// truthful. A video-interview action may ONLY be projected for these states
// (rule 1), and only for a genuinely upcoming (active, not-past) appointment.
export const INTERVIEW_JOIN_ACTION_STATES = new Set(['scheduled', 'join_today']);

// Canonical states in which the interview is OVER from the applicant's side.
// While in one of these states NO interview next action (join / questionnaire /
// "waiting") may be projected — the genuine current recruitment step (e.g.
// mandatory training) owns the next action, so we never contradict the
// canonical "Interview successful/completed/…" surface (rules 2 & 4).
export const INTERVIEW_CONCLUDED_STATES = new Set([
  'awaiting_outcome',
  'under_review',
  'successful',
  'unsuccessful',
]);

/**
 * True when the applicant's interview has concluded (awaiting outcome / under
 * review / successful / unsuccessful). Callers must NOT project any interview
 * next action in this case.
 * @param {?{state?: string}} interviewState canonical projection from backend
 */
export function isInterviewStepConcluded(interviewState) {
  const stateKey = interviewState && interviewState.state ? interviewState.state : null;
  return stateKey ? INTERVIEW_CONCLUDED_STATES.has(stateKey) : false;
}

function appointmentIsGenuinelyUpcoming(appointment) {
  if (!appointment) return false;
  const status = String(appointment.status || '').toLowerCase();
  const isActive = status === 'scheduled' || status === 'confirmed';
  // A past active appointment (is_past) is never "upcoming" (rule 3).
  return isActive && appointment.is_past !== true;
}

/**
 * Whether a "Join your video interview" NEXT action may be projected.
 * Requires: a video link, a genuinely upcoming (active, not-past) appointment,
 * AND — when a canonical state is present — that the state is scheduled or
 * join_today. Never for concluded/past interviews (rules 1–3).
 * @param {object} args
 * @param {?{state?: string}} args.interviewState canonical projection
 * @param {?object} args.appointment resolved appointment (status/is_past/video_meeting_link)
 * @returns {boolean}
 */
export function canProjectJoinInterviewAction({ interviewState, appointment } = {}) {
  if (!appointment || !appointment.video_meeting_link) return false;
  if (!appointmentIsGenuinelyUpcoming(appointment)) return false;
  const stateKey = interviewState && interviewState.state ? interviewState.state : null;
  if (stateKey) return INTERVIEW_JOIN_ACTION_STATES.has(stateKey);
  // Legacy payload without a canonical state: allow only a genuinely upcoming
  // active appointment with a link (still truthful; never past/concluded).
  return true;
}

/**
 * Resolve the SINGLE interview surface to render. Guarantees no duplication:
 *   'appointment' -> the upcoming (scheduled/confirmed, not past) appointment card
 *   'journey'     -> the full journey card (locked/questionnaire/choose_slot/
 *                    awaiting_scheduling/awaiting_outcome/under_review/
 *                    successful/unsuccessful)
 *   null          -> render nothing
 * @returns {'appointment'|'journey'|null}
 */
export function resolveInterviewSurface({ isActiveEmployee, interviewState, hasScheduledInterview, wizardShowsInterview = false }) {
  if (isActiveEmployee) return null;
  if (hasScheduledInterview) return 'appointment';
  if (shouldShowInterviewJourney({ isActiveEmployee, interviewState, wizardShowsInterview })) return 'journey';
  return null;
}
